import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const Pricing = () => {
  const plans = [
    {
      name: 'Starter',
      price: '$97',
      period: 'per month',
      description: 'Perfect for small businesses getting started with data analytics',
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      features: [
        'Up to 3 data source connectors',
        'Real-time dashboard analytics',
        'Basic reporting templates',
        'Email support',
        '30-day data retention',
        'Standard OAuth integrations',
        'Basic data export (CSV)',
        'Mobile-responsive dashboard'
      ],
      popular: false,
      buttonText: 'Start Free Trial',
      buttonVariant: 'outlined'
    },
    {
      name: 'Professional',
      price: '$297',
      period: 'per month',
      description: 'Advanced analytics and integrations for growing businesses',
      icon: <BusinessIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      features: [
        'Up to 10 data source connectors',
        'Advanced dashboard customization',
        'Custom reporting templates',
        'Priority support + live chat',
        '12-month data retention',
        'All OAuth integrations included',
        'Advanced data export (CSV, JSON, API)',
        'AI-powered insights and recommendations',
        'Custom data attribution modeling',
        'Slack integration for alerts',
        'White-label dashboard options'
      ],
      popular: true,
      buttonText: 'Start Free Trial',
      buttonVariant: 'contained'
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'contact us',
      description: 'Tailored solutions for large organizations with complex needs',
      icon: <StarIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      features: [
        'Unlimited data source connectors',
        'Fully customizable dashboards',
        'Unlimited custom templates',
        'Dedicated account manager',
        'Unlimited data retention',
        'Custom integrations development',
        'Advanced API access + webhooks',
        'Advanced AI chat with Claude integration',
        'Custom data modeling and ETL',
        'SSO and enterprise security',
        'SLA guarantees',
        'On-premises deployment options'
      ],
      popular: false,
      buttonText: 'Contact Sales',
      buttonVariant: 'outlined'
    }
  ];

  const supportedPlatforms = [
    'Shopify', 'Amazon Ads', 'Amazon Seller Partner', 'Google Ads', 
    'Google Analytics', 'Google Search Console', 'Meta Ads', 
    'Pinterest Business', 'TikTok for Business', 'Klaviyo', 'SMSBump', 'Slack'
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Paper elevation={0} sx={{ p: 0, backgroundColor: 'transparent' }}>
        {/* Header Section */}
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography variant="h2" component="h1" gutterBottom color="primary">
            Simple, Transparent Pricing
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
            Choose the plan that scales with your business. All plans include our core analytics platform 
            with real-time data integration from 12+ marketing and e-commerce platforms.
          </Typography>
          
          {/* Supported Platforms */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom color="text.primary">
              Supported Platforms:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
              {supportedPlatforms.map((platform) => (
                <Chip 
                  key={platform} 
                  label={platform} 
                  variant="outlined" 
                  size="small"
                  sx={{ borderColor: 'primary.light' }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={plan.name}>
              <Card 
                elevation={plan.popular ? 8 : 2}
                sx={{ 
                  height: '100%', 
                  position: 'relative',
                  border: plan.popular ? 2 : 0,
                  borderColor: plan.popular ? 'secondary.main' : 'transparent',
                  transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                  transition: 'transform 0.2s ease-in-out'
                }}
              >
                {plan.popular && (
                  <Chip
                    label="Most Popular"
                    color="secondary"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      zIndex: 1,
                      fontWeight: 'bold'
                    }}
                  />
                )}
                
                <CardHeader
                  avatar={plan.icon}
                  title={
                    <Typography variant="h4" component="h3" color="text.primary">
                      {plan.name}
                    </Typography>
                  }
                  subheader={
                    <Typography variant="body1" color="text.secondary">
                      {plan.description}
                    </Typography>
                  }
                  sx={{ pb: 2 }}
                />

                <CardContent sx={{ pt: 0 }}>
                  {/* Price */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography 
                      variant="h3" 
                      component="div" 
                      color="primary"
                      sx={{ fontWeight: 'bold', lineHeight: 1 }}
                    >
                      {plan.price}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {plan.period}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Features */}
                  <List sx={{ py: 0 }}>
                    {plan.features.map((feature, featureIndex) => (
                      <ListItem key={featureIndex} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.primary'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {/* CTA Button */}
                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Button
                      variant={plan.buttonVariant}
                      color={plan.popular ? 'secondary' : 'primary'}
                      size="large"
                      fullWidth
                      sx={{ 
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold'
                      }}
                      href={plan.name === 'Enterprise' ? 'mailto:sales@superchargeme.com?subject=Enterprise Plan Inquiry' : '#'}
                    >
                      {plan.buttonText}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Information */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom color="text.primary">
            All Plans Include:
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="h6" color="primary" gutterBottom>
                  ✓ Free 14-Day Trial
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Test drive all features with no commitment
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="h6" color="primary" gutterBottom>
                  ✓ No Setup Fees
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Start immediately with guided onboarding
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="h6" color="primary" gutterBottom>
                  ✓ Cancel Anytime
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  No long-term contracts or cancellation fees
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Section */}
        <Box sx={{ mt: 8, p: 4, backgroundColor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom color="text.primary">
            Questions About Pricing?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Our team is here to help you choose the right plan for your business needs.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              color="primary" 
              href="mailto:sales@superchargeme.com?subject=Pricing Inquiry"
              sx={{ minWidth: 150 }}
            >
              Contact Sales
            </Button>
            <Button 
              variant="outlined" 
              color="primary" 
              href="mailto:support@superchargeme.com?subject=Support Inquiry"
              sx={{ minWidth: 150 }}
            >
              Get Support
            </Button>
          </Box>
        </Box>

        {/* Footer Note */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            All prices are in USD. Payment processing handled securely outside of Shopify's billing system.
            <br />
            Enterprise customers can arrange custom payment terms and invoicing.
          </Typography>
        </Box>

      </Paper>
    </Container>
  );
};

export default Pricing;
